import { MoneyMapStatus } from '@fruitful/types';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonVariant,
    CircleDotIcon,
    CircleIcon,
    Icon,
    LabelRegular,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { TextError } from '../../common/components/InlineError';

interface MoneyMapStatusBlurbProps {
    status: string;
    blurb: string;
    isActive?: boolean;
}

const MoneyMapStatusBlurb = ({
    status,
    blurb,
    isActive = false,
}: MoneyMapStatusBlurbProps) => {
    return (
        <YStack
            gap={4}
            paddingBottom={24}
            paddingLeft={8}
            borderLeftWidth={2}
            borderLeftColor="$gray200"
            // @ts-expect-error this is a valid RNW attribute
            borderLeftStyle="dashed"
        >
            <XStack gap={isActive ? 12 : 8}>
                <View
                    marginLeft={isActive ? -16 : -12}
                    width={16}
                    alignSelf="center"
                    backgroundColor="$white"
                >
                    <Icon
                        color={isActive ? '$black' : '$gray700'}
                        icon={isActive ? CircleDotIcon : CircleIcon}
                        size={isActive ? 16 : 6}
                    />
                </View>
                <LabelRegular>{status}</LabelRegular>
            </XStack>
            <BodySmallRegular color="$gray900" marginLeft={12}>
                {blurb}
            </BodySmallRegular>
        </YStack>
    );
};

interface MoneyMapStatusChangeConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    status: MoneyMapStatus;
    onPressConfirm: () => void;
}

const MoneyMapStatusChangeConfirmModal = ({
    isOpen,
    onClose,
    status,
    onPressConfirm,
}: MoneyMapStatusChangeConfirmModalProps) => {
    if (status !== MoneyMapStatus.DRAFT && status !== MoneyMapStatus.PREVIEW) {
        return (
            <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
                <TextError message="Money map is not expected status." />
            </GroveSimpleModal>
        );
    }

    const ctaText = status === MoneyMapStatus.DRAFT ? 'Publish' : 'Finalize';
    const statusText =
        status === MoneyMapStatus.DRAFT
            ? 'You are about to move this Money Map from Draft to Published. Once published, members will be able to preview. This action cannot be undone.'
            : 'You are about to move this Money Map from Published to Finalized. Once finalized, you will no longer be able to edit, and members will be able to open proposed accounts and implement Income Split rules. This action cannot be undone.';
    const footerText =
        status === MoneyMapStatus.DRAFT
            ? null
            : 'Any previously finalized Money Maps will be archived.';

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <YStack gap={24} width={480}>
                <BodyMediumRegular marginTop={16}>{ctaText}</BodyMediumRegular>
                <BodySmallRegular color="$gray900">
                    {statusText}
                </BodySmallRegular>
                <YStack marginLeft={36}>
                    <MoneyMapStatusBlurb
                        status="Draft"
                        blurb={'Guide can make edits.\nNo member visibility.'}
                        isActive={status === MoneyMapStatus.DRAFT}
                    />
                    <MoneyMapStatusBlurb
                        status="Publish"
                        blurb={
                            'Guide can make edits.\nMember can preview only.'
                        }
                        isActive={status === MoneyMapStatus.PREVIEW}
                    />
                    <MoneyMapStatusBlurb
                        status="Finalize"
                        blurb={
                            'No longer editable.\nMember can action/implement.'
                        }
                    />
                    <MoneyMapStatusBlurb
                        status="Archive"
                        blurb={'No longer editable.\nNo member visibility.'}
                    />
                </YStack>
                {footerText != null && (
                    <BodySmallRegular color="$gray900">
                        {footerText}
                    </BodySmallRegular>
                )}
                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    gap={16}
                >
                    <Button
                        text="Cancel"
                        variant={ButtonVariant.SECONDARY}
                        onPress={() => {
                            onClose();
                        }}
                        buttonProps={{
                            flex: 1,
                        }}
                    />
                    <Button
                        text={ctaText}
                        variant={ButtonVariant.PRIMARY}
                        onPress={() => {
                            onPressConfirm();
                            onClose();
                        }}
                        buttonProps={{
                            flex: 1,
                        }}
                    />
                </XStack>
            </YStack>
        </GroveSimpleModal>
    );
};

export default MoneyMapStatusChangeConfirmModal;
